// src/components/Sales/AttentionList.tsx
// 要対応一覧: 費用未確定で保留中の売上データと、売上への計上漏れを表示する

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Typography,
    Box,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { salesService } from '../../services/salesService';
import { AttentionResponse } from '../../types/sales';
import { useAuth } from 'react-oidc-context';

export const AttentionList: React.FC = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState<AttentionResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = auth.user?.access_token;
            const result = await salesService.getAttentionList(token);
            setData(result);
        } catch (err: any) {
            console.error('要対応一覧の取得に失敗しました:', err);
            setError(err.message || '要対応一覧の取得に失敗しました');
        } finally {
            setLoading(false);
        }
    }, [auth.user?.access_token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
    }

    const pendingDrafts = data?.pendingDrafts || [];
    const orphans = data?.orphans || [];
    const mismatches = data?.mismatches || [];
    const uncalculatedMonths = data?.uncalculatedMonths || [];
    const yen = (n: number) => `¥${(n || 0).toLocaleString()}`;

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button startIcon={<RefreshIcon />} onClick={fetchData} size="small">
                    最新の状態に更新
                </Button>
            </Box>

            {/* 保留中（費用未確定）一覧 */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <HourglassBottomIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                        金額の入力待ち（保留中）: {pendingDrafts.length}件
                    </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    業者発注分・諸経費に金額未入力（0円）の明細が残っているため、売上に計上されていない工事です。
                    <strong>行をクリックすると工事編集ページに移動します。</strong>
                    金額を入力すると、入力した月の売上に自動計上されます。
                </Typography>

                {pendingDrafts.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                        保留中の工事はありません
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>見積番号</TableCell>
                                    <TableCell>得意先</TableCell>
                                    <TableCell>現場名</TableCell>
                                    <TableCell>担当</TableCell>
                                    <TableCell>請求月</TableCell>
                                    <TableCell>未入力の明細</TableCell>
                                    <TableCell align="right">保留日数</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendingDrafts.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        onClick={() => navigate(`/construction/${row.construction_id}/edit`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{row.Quotation_No}</TableCell>
                                        <TableCell>{row.Customer_Name}</TableCell>
                                        <TableCell>{row.Site_Name}</TableCell>
                                        <TableCell>{row.Superviser}</TableCell>
                                        <TableCell>{row.billing_month}</TableCell>
                                        <TableCell>
                                            {row.unconfirmed_vendors > 0 && (
                                                <Chip label={`業者発注分 ${row.unconfirmed_vendors}件`}
                                                      size="small" color="warning" sx={{ mr: 0.5 }} />
                                            )}
                                            {row.unconfirmed_expenses > 0 && (
                                                <Chip label={`諸経費 ${row.unconfirmed_expenses}件`}
                                                      size="small" color="warning" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                color={row.pending_days > 60 ? 'error' : 'textPrimary'}
                                                fontWeight={row.pending_days > 60 ? 'bold' : 'normal'}
                                            >
                                                {row.pending_days}日
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* 計上漏れ一覧 */}
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ReportProblemIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                        売上への計上漏れ: {orphans.length}件
                    </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    請求書は作成済みなのに、売上データが存在しない工事です。
                    通常は自動処理されるため、ここに表示された場合はシステム担当者に連絡してください。
                </Typography>

                {orphans.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                        計上漏れはありません
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>見積番号</TableCell>
                                    <TableCell>得意先</TableCell>
                                    <TableCell>現場名</TableCell>
                                    <TableCell>担当</TableCell>
                                    <TableCell>請求月</TableCell>
                                    <TableCell>作成済みにした日</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orphans.map((row) => (
                                    <TableRow key={row.invoice_id} hover>
                                        <TableCell>{row.Quotation_No}</TableCell>
                                        <TableCell>{row.Customer_Name}</TableCell>
                                        <TableCell>{row.Site_Name}</TableCell>
                                        <TableCell>{row.Superviser}</TableCell>
                                        <TableCell>{row.billing_month}</TableCell>
                                        <TableCell>{row.completed_date || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* 差異あり一覧（計上後に費用・請求額が変わった） */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CompareArrowsIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                        計上値と現在値の差異: {mismatches.length}件
                    </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    計上済み（準備完了・確定済み）の後に、費用（諸経費・業者発注・日報）や請求金額が変更され、
                    売上データの値が実態とズレている工事です。再集計するか、値を確認してください。
                </Typography>

                {mismatches.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                        差異はありません
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>見積番号</TableCell>
                                    <TableCell>得意先</TableCell>
                                    <TableCell>現場名</TableCell>
                                    <TableCell>担当</TableCell>
                                    <TableCell>請求月</TableCell>
                                    <TableCell>差異の内容（計上値 → 現在値）</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {mismatches.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.Quotation_No}</TableCell>
                                        <TableCell>{row.Customer_Name}</TableCell>
                                        <TableCell>{row.Site_Name}</TableCell>
                                        <TableCell>{row.Superviser}</TableCell>
                                        <TableCell>{row.billing_month}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {row.diffFields.map((f) => (
                                                    <Chip
                                                        key={f.label}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label={`${f.label}: ${yen(f.stored)} → ${yen(f.current)}`}
                                                    />
                                                ))}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* 未集計の月（集計し忘れ） */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventBusyIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                        未集計の月: {uncalculatedMonths.length}件
                    </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    完了済みの請求があるのに、月次集計が一度も実行されていない月です（当月は除く）。
                    その月の売上集計を実行してください。
                </Typography>

                {uncalculatedMonths.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                        未集計の月はありません
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>請求月</TableCell>
                                    <TableCell align="right">完了済みの請求数</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {uncalculatedMonths.map((row) => (
                                    <TableRow key={row.billing_month} hover>
                                        <TableCell>{row.billing_month}</TableCell>
                                        <TableCell align="right">{row.completed_count}件</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </>
    );
};
